import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { useAuthStore, API_BASE, apiFetch } from '../store/auth'
import { useChatStore } from '../store/chat'
import OrganizationSelector from './OrganizationSelector'
import CreateOrganizationModal from './CreateOrganizationModal'
import ChatDrawer from './ChatDrawer'
import ChatFloatingButton from './ChatFloatingButton'
import NavGroup from './NavGroup'
import { ToastContainer, NotificationBell } from './index'
import MobileNav from './MobileNav'

export default function AppLayout() {
  const { user, logout } = useAuthStore()
  const { isOpen, setOpen, toggleOpen } = useChatStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await apiFetch(`${API_BASE}/auth/logout`, { method: 'POST' })
    } catch {}
    await logout()
    navigate('/login')
  }

  // Keyboard shortcut: Cmd+K / Ctrl+K to toggle chat
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      toggleOpen()
    }
    // Also close on Escape
    if (e.key === 'Escape' && isOpen) {
      setOpen(false)
    }
  }, [toggleOpen, isOpen, setOpen])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Navigation groups configuration
  const navGroups = [
    {
      icon: '📄',
      label: 'Laskutus',
      items: [
        { to: '/invoices', label: 'Laskut' },
        { to: '/invoices/overdue', label: 'Erääntyneet', badge: 0 }, // Badge will be populated dynamically
        { to: '/settings/invoices', label: 'Asetukset' },
      ],
    },
    {
      icon: '📈',
      label: 'Raportit ja Verot',
      items: [
        { to: '/reports', label: 'Raportit' },
        { to: '/journal', label: 'Päiväkirja' },
        { to: '/general-ledger', label: 'Pääkirja' },
        { to: '/vat-returns', label: 'ALV-ilmoitukset' },
        { to: '/tax-reports', label: 'Veroilmoitukset' },
      ],
    },
    {
      icon: '🔄',
      label: 'Täsmäytys',
      items: [
        { to: '/reconciliation', label: 'Pankkitäsmäytys' },
        { to: '/receipt-matching', label: 'Tositteet' },
        { to: '/reconciliation/ai', label: 'AI-ehdotukset' },
      ],
    },
    {
      icon: '⚙️',
      label: 'Asetukset',
      items: [
        { to: '/settings/organization', label: 'Organisaatio' },
        { to: '/settings/integrations', label: 'Integraatiot' },
        { to: '/category-budget-limits', label: 'Budjetit' },
        { to: '/settings/skills', label: 'Skillit' },
        { to: '/settings/notifications', label: 'Ilmoitukset' },
        { to: '/settings/privacy', label: 'Tietosuoja' },
      ],
    },
  ]

  // Single navigation items (not in groups)
  const singleNavItems = [
    { to: '/dashboard', label: '📊 Dashboard' },
    { to: '/transactions', label: '💰 Tapahtumat' },
    { to: '/sales', label: '🛒 Myynti' },
    { to: '/members', label: '👥 Jäsenet' },
    { to: '/files', label: '📎 Tiedostot' },
  ]

  // Check if a single nav item is active
  const isSingleNavActive = (to: string) => {
    if (to === '/dashboard') {
      return location.pathname === '/dashboard'
    }
    return location.pathname === to || location.pathname.startsWith(to + '/')
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className="hidden lg:flex w-64 bg-indigo-900 text-white flex-col shrink-0">
        <div className="p-5 border-b border-indigo-800">
          <h1 className="text-2xl font-bold">📚 Kirstunvartija</h1>
          {/* Organization selector */}
          <div className="mt-3">
            <OrganizationSelector onCreateNew={() => setIsCreateModalOpen(true)} />
          </div>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-3 overflow-y-auto">
          {/* Single navigation items */}
          {singleNavItems.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`block px-3 py-2 rounded-lg transition text-sm ${
                isSingleNavActive(to)
                  ? 'bg-indigo-700 text-white'
                  : 'text-indigo-200 hover:bg-indigo-700 hover:text-white'
              }`}
            >
              {label}
            </Link>
          ))}

          {/* Navigation groups */}
          {navGroups.map((group) => (
            <NavGroup
              key={group.label}
              icon={group.icon}
              label={group.label}
              items={group.items}
            />
          ))}
        </nav>

        <div className="p-4 border-t border-indigo-800">
          <div className="text-xs text-indigo-300 truncate mb-2">{user?.email}</div>
          <button
            onClick={handleLogout}
            className="w-full text-sm text-indigo-300 hover:text-white hover:bg-indigo-700 rounded px-3 py-1.5 transition text-left"
          >
            🚪 Kirjaudu ulos
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-72 bg-indigo-900 text-white flex flex-col z-50 lg:hidden">
            <div className="p-5 border-b border-indigo-800 flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold">📚 Kirstunvartija</h1>
                <div className="mt-2">
                  <OrganizationSelector onCreateNew={() => setIsCreateModalOpen(true)} />
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-indigo-700 transition"
                aria-label="Sulje valikko"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <nav className="flex-1 py-4 space-y-1 px-3 overflow-y-auto">
              {/* Single navigation items */}
              {singleNavItems.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-3 py-3 rounded-lg transition text-sm ${
                    isSingleNavActive(to)
                      ? 'bg-indigo-700 text-white'
                      : 'text-indigo-200 hover:bg-indigo-700 hover:text-white'
                  }`}
                >
                  {label}
                </Link>
              ))}

              {/* Navigation groups */}
              {navGroups.map((group) => (
                <NavGroup
                  key={group.label}
                  icon={group.icon}
                  label={group.label}
                  items={group.items.map(item => ({ ...item, onClick: () => setIsMobileMenuOpen(false) }))}
                />
              ))}
            </nav>

            <div className="p-4 border-t border-indigo-800">
              <div className="text-xs text-indigo-300 truncate mb-2">{user?.email}</div>
              <button
                onClick={handleLogout}
                className="w-full text-sm text-indigo-300 hover:text-white hover:bg-indigo-700 rounded px-3 py-2 transition text-left"
              >
                🚪 Kirjaudu ulos
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-indigo-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 -ml-2 rounded-lg hover:bg-indigo-700 transition min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Avaa valikko"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-lg font-bold truncate">📚 Kirstunvartija</h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <OrganizationSelector compact onCreateNew={() => setIsCreateModalOpen(true)} />
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1">
          <Outlet />
        </div>
        
        {/* Footer - hidden on smallest screens */}
        <footer className="bg-gray-50 border-t border-gray-200 py-4 px-4 sm:px-6 text-sm text-gray-600">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div>
              © {new Date().getFullYear()} Kirstunvartija
            </div>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-indigo-600">
                Käyttöehdot
              </Link>
              <Link to="/settings/privacy" className="hover:text-indigo-600">
                Tietosuoja
              </Link>
            </div>
          </div>
        </footer>

        {/* Mobile Bottom Navigation */}
        <MobileNav currentPath={location.pathname} />
      </main>

      {/* Chat Drawer */}
      <ChatDrawer isOpen={isOpen} onClose={() => setOpen(false)} />

      {/* Floating Chat Button - hidden on mobile (bottom nav takes its place) */}
      <div className="hidden lg:block">
        <ChatFloatingButton onClick={toggleOpen} />
      </div>

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  )
}
